import {
  Grid,
  LinearProgress,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
  Button,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Box from "@mui/material/Box";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarExport,
} from "@mui/x-data-grid";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Reports } from "../../../../../app/slices/reportsSlice";
import { SelectRepresentative } from "../../../../../components/Selectors/SelectRepresentative";
import { HiOutlineUserCircle } from "react-icons/hi2";

// Brand palette (Dejlah primary blue)
import {
  MobileEmpty,
  MobileListItem,
  MobileListShell,
  MobileSkeletonList,
} from "../../../../../components/MobileList";

const BLUE = "#0C86B6";

// Clickable cash value rendered as a button-style pill so it stays easy to
// see and tap even when the amount is zero.
const CashButton = ({ id, cash }) => (
  <Button
    onClick={() => {
      localStorage.setItem("isWholeSale", "0");
    }}
    href={`/app/wallets/transactions/${id}`}
    target="_blank"
    rel="noopener noreferrer"
    variant="outlined"
    size="small"
    sx={{
      minWidth: 64,
      py: 0.25,
      px: 1.25,
      borderColor: "rgba(12,134,182,0.35)",
      color: BLUE,
      fontWeight: 700,
      lineHeight: 1.4,
      textTransform: "none",
      "&:hover": {
        borderColor: "rgba(12,134,182,0.55)",
        bgcolor: "rgba(12,134,182,0.08)",
      },
    }}
  >
    {(cash ?? 0).toLocaleString()}
  </Button>
);

const RepDetailsTabel = () => {
  const dispatch = useDispatch();

  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down("md"));

  const _RepDetails = useSelector((state) => state.reports.repDetails);
  const loading = useSelector((state) => state.reports.reports.loading);
  const _rep = useSelector((state) => state.user.usersList.data);

  const [search, setSearch] = useState({
    is_client: false,
    rep_id: null,
  });

  const columns = [
    {
      field: "name",
      headerName: "الاسم",
      flex: 1,
    },
    {
      field: "mobile",
      headerName: "رقم الهاتف",
      flex: 1,
    },
    {
      field: "cash",
      headerName: "الكاش (IQD)",
      flex: 1,
      renderCell: (params) => (
        <CashButton id={params.row.id} cash={params.row?.cash} />
      ),
    },
    {
      field: "balance",
      headerName: "الرصيد (IQD)",
      flex: 1,
      renderCell: (params) => {
        return <>{params.row?.balance?.toLocaleString()}</>;
      },
    },
    {
      field: "debt",
      headerName: "الديون (IQD)",
      flex: 1,
      renderCell: (params) => {
        return <>{params.row?.debt?.toLocaleString()}</>;
      },
    },
    {
      field: "total_target",
      headerName: "المبيعات هذا الشهر (IQD)",
      flex: 1,
      renderCell: (params) => {
        return <>{params.row?.total_target?.toLocaleString()}</>;
      },
    },
    {
      field: "rep_pos_count",
      headerName: "عدد نقاط البيع",
      flex: 1,
    },
    {
      field: "total_pos_balance",
      headerName: "رصيد النقاط (IQD)",
      flex: 1,
      renderCell: (params) => params.row?.total_pos_balance?.toLocaleString(),
    },
    {
      field: "total_pos_debt",
      headerName: "ديون النقاط (IQD)",
      flex: 1,
      renderCell: (params) => params.row?.total_pos_debt?.toLocaleString(),
    },
    // {
    //   field: "last_transaction",
    //   headerName: "رقم آخر معاملة",
    //   flex: 1,
    //   valueGetter: (params) => params.row.last_transaction ? params.row.last_transaction.number : "",
    //   renderCell: (params) => {
    //     return (
    //       <>
    //         {params.row.last_transaction ? params.row.last_transaction.number : "---"}
    //       </>
    //     );
    //   },
    // },
    // {
    //   field: "created_at",
    //   headerName: "تاريخ آخر معاملة",
    //   flex: 1,
    //   valueGetter: (params) => params.row.last_transaction ? dayjs(params.row.last_transaction.created_at).format(" YYYY-MM-DD / MM:hha ") : "",
    //   renderCell: (params) => {
    //     return (
    //       <>
    //         {params.row.last_transaction ? dayjs(params.row.last_transaction.created_at).format(" YYYY-MM-DD / MM:hha ") : "---"}
    //       </>
    //     );
    //   },
    // },
  ];

  const handleSearch = () => {
    dispatch(
      Reports.getAll({
        is_client: search.is_client,
        rep_id: search.rep_id ?? "",
      }),
    );
  };

  const customToolbar = () => (
    <GridToolbarContainer>
      <GridToolbarExport
        csvOptions={{
          fileName: "تقرير تفاصيل المندوب | نظام دجلة",
          utf8WithBom: true,
        }}
      />
    </GridToolbarContainer>
  );

  return (
    <Card>
      <CardContent>
        <Grid container rowSpacing={1} columnSpacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <SelectRepresentative
              onChange={(event, value) => {
                setSearch({
                  ...search,
                  rep_id: value ? value.id : null,
                });
              }}
              value={_rep?.filter((x) => x.id === search.rep_id)[0] ?? null}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={search.is_client === 1 ? true : false}
                  onChange={(e) =>
                    setSearch({
                      ...search,
                      is_client: e.target.checked ? 1 : 0,
                    })
                  }
                />
              }
              label="العملاء"
            />
            <Button variant="contained" color="primary" onClick={handleSearch}>
              بحث
            </Button>
          </Grid>
          {smallScreen ? (
            <Grid item xs={12}>
              <MobileListShell>
                {loading ? (
                  <MobileSkeletonList count={4} fields={6} />
                ) : _RepDetails?.data?.length > 0 ? (
                  _RepDetails.data.map((data, index) => (
                    <MobileListItem
                      key={data.id ?? index}
                      avatar={<HiOutlineUserCircle />}
                      title={data?.name}
                      subtitle={data?.mobile || "—"}
                      highlight={{
                        label: "الكاش (IQD)",
                        value: <CashButton id={data.id} cash={data?.cash} />,
                      }}
                      fields={[
                        {
                          label: "الرصيد (IQD)",
                          value: data?.balance?.toLocaleString(),
                        },
                        {
                          label: "الديون (IQD)",
                          value: data?.debt?.toLocaleString(),
                        },
                        {
                          label: "المبيعات هذا الشهر (IQD)",
                          value: data?.total_target?.toLocaleString(),
                        },
                        {
                          label: "عدد نقاط البيع",
                          value: data?.rep_pos_count,
                        },
                        {
                          label: "رصيد النقاط (IQD)",
                          value: data?.total_pos_balance?.toLocaleString(),
                        },
                        {
                          label: "ديون النقاط (IQD)",
                          value: data?.total_pos_debt?.toLocaleString(),
                        },
                      ]}
                    />
                  ))
                ) : (
                  <MobileEmpty />
                )}
              </MobileListShell>
            </Grid>
          ) : (
            <Grid item xs={12}>
              <Box sx={{ height: "70vh", width: "100%" }}>
                {_RepDetails.data ? (
                  <DataGrid
                    rows={_RepDetails.data}
                    columns={columns}
                    rowCount={_RepDetails.total}
                    loading={loading}
                    rowHeight={40}
                    selectionModel={[]}
                    rowsPerPageOptions={[1000]}
                    components={{
                      LoadingOverlay: LinearProgress,
                      Toolbar: customToolbar,
                    }}
                  />
                ) : (
                  <LinearProgress />
                )}
              </Box>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default RepDetailsTabel;
